import { useState } from "react";

import { useGoals } from "../../hooks";
import { useFeatureFlag, FEATURE_FLAGS } from "../../contexts";
import { getCurrencySymbol } from "../../utils/currencyUtils";
import { TargetIcon, EyeIcon } from "./icons";
import { GoalBreakdownModal } from "./GoalBreakdownModal";
import { TeamGoalsModal } from "../../components/TeamGoalsModal/TeamGoalsModal";
import {
  DASHBOARD_GOAL_TYPE_LIST,
  DASHBOARD_GOAL_TYPES,
  computeGoalTargets,
  getDashboardGoals,
  getTargetPct,
  getQuarterLabel,
  getSummaryCurrency,
} from "./constants";
import { Tooltip } from "flowbite-react";
import { useGoalAmountFormat } from "./useGoalAmountFormat";

// Small inline "Estimated" marker for the widget. PAN-587: shown next to a
// bucket target that came from the resolver's live compute rather than a
// leader-committed frozen row. Style mirrors the widget's other muted meta
// labels — text-xs slate-500, no border, no background.
const EstimatedPill = () => (
  <span className="ml-1 text-xs font-normal text-slate-500">Estimated</span>
);

const GoalTooltipItems = ({
  sym,
  label,
  color,
  current,
  target,
  pipeline,
  estimated,
}) => {
  const pct = target > 0 ? Math.min(100, (current / target) * 100) : 0;
  const fmt = useGoalAmountFormat();
  return (
    <div className="flex min-w-36 flex-col gap-1">
      <div className="flex items-center justify-between gap-2 text-sm">
        <div className="flex w-full items-center gap-1">
          <span className={`h-2.5 w-2.5 rounded-full ${color}`} />
          {label}
          {estimated && <EstimatedPill />}
        </div>
        <span>
          <span className="font-bold">
            {sym}
            {fmt(current)}
          </span>
          /{sym}
          {fmt(target)}
        </span>
      </div>
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-600">
        <div
          className={`h-full rounded-full ${color} transition-all duration-700`}
          style={{ width: `${pct}%` }}
        />
      </div>
      {pipeline > 0 && (
        <div className="flex items-center justify-between text-xs text-slate-400">
          <span>Pipeline</span>
          <span>
            {sym}
            {fmt(pipeline)}
          </span>
        </div>
      )}
    </div>
  );
};

const GoalTooltip = ({ sym, goals }) => {
  return (
    <div className="flex flex-row gap-8 p-3">
      {goals.map((row) => (
        <GoalTooltipItems key={row.label} sym={sym} {...row} />
      ))}
    </div>
  );
};

// ARR is a display-only ×12 transform on MRR (PAN-523). Applied at the RENDER layer only —
// computeGoalTargets and getPilotPool remain MRR-scoped (persistence stores MRR); the two
// modals take `revenueUnit` and annualize what they display. Do NOT push the multiplier
// into the shared helpers; the configuration surfaces must edit the stored MRR value.
const annualize = (value, revenueUnit) =>
  revenueUnit === "ARR" ? value * 12 : value;

export const RevenueGoalCard = ({
  summary,
  nrrProgress,
  revenueUnit = "MRR",
}) => {
  const [gearModalOpen, setGearModalOpen] = useState(false);
  const [eyeModalOpen, setEyeModalOpen] = useState(false);
  const { goals, loading } = useGoals();

  const goalsByType = getDashboardGoals(goals);
  const hasDashboardGoals = DASHBOARD_GOAL_TYPE_LIST.some(
    (t) => t in goalsByType,
  );
  // Three-bucket additive targets (PAN-406 widget, PAN-520 pilot goal). Single source of
  // truth: computeGoalTargets — the modals call the same helper, so a divergent inline
  // formula here would silently show a widget target that disagrees with the settings
  // surface. Attrition folds into the Renewal-realized-vs-target gap and has no dedicated
  // bucket.
  const pcts = {
    retention: getTargetPct(goalsByType, DASHBOARD_GOAL_TYPES.retention),
    expansion: getTargetPct(goalsByType, DASHBOARD_GOAL_TYPES.expansion),
    pilot: getTargetPct(goalsByType, DASHBOARD_GOAL_TYPES.pilot),
  };
  const {
    renewalTarget,
    expansionTarget,
    pilotTarget,
    pilotsMapped,
    estimated,
  } = computeGoalTargets(summary, nrrProgress, pcts);
  // PAN-587: FE gate stays off by default. When on and any displayed bucket
  // came from a live compute, surface the "Estimated" label so a leader knows
  // the target has not yet been committed for this quarter.
  const estimatedPillEnabled = useFeatureFlag(
    FEATURE_FLAGS.ESTIMATED_GOAL_PILL,
  );
  const fmt = useGoalAmountFormat();
  const anyBucketEstimated =
    estimated.renewal ||
    estimated.expansion ||
    (pilotsMapped && estimated.pilots);
  const showTopLineEstimatedPill = estimatedPillEnabled && anyBucketEstimated;

  // Realized current-quarter MRR from the cohort-correct drivers endpoint. The
  // three-bucket additive shape (renewal + expansion + pilots) always sums non-negative,
  // no floor needed. Fall back to 0 while loading/failed (never blank, never crash).
  //
  // PAN-568 resolves the previous cohort mismatch between `current` (deal-cohort
  // realized) and `target` (previously standing customer pool): when the API
  // returns a frozen per-quarter goal, `target` is now measured against the same
  // fiscal quarter, so `current / target` is a coherent ring. Legacy pool-×-%
  // remains the fallback for teams the backend hasn't materialized.
  const renewalCurrent = Number(nrrProgress?.renewal?.amount ?? 0);
  const expansionCurrent = Number(nrrProgress?.expansion?.amount ?? 0);
  const pilotsCurrent = Number(nrrProgress?.pilots?.amount ?? 0);
  const renewalPipeline = Number(nrrProgress?.renewal?.pipeline ?? 0);
  const expansionPipeline = Number(nrrProgress?.expansion?.pipeline ?? 0);
  const pilotsPipeline = Number(nrrProgress?.pilots?.pipeline ?? 0);
  const hasPipeline =
    renewalPipeline > 0 ||
    expansionPipeline > 0 ||
    (pilotsMapped && pilotsPipeline > 0);
  const nrrCurrent = nrrProgress
    ? renewalCurrent + expansionCurrent + (pilotsMapped ? pilotsCurrent : 0)
    : 0;
  const goalData = {
    target: renewalTarget + expansionTarget + pilotTarget,
    renewalTarget,
    expansionTarget,
    pilotTarget,
    current: nrrCurrent,
    renewalCurrent,
    expansionCurrent,
    pilotsCurrent,
  };

  // Prefer the NRR endpoint's own currency (it returns get_team_currency); fall back
  // to the summary's currency only when nrrProgress hasn't resolved. Avoids briefly
  // rendering a non-EUR team's amounts with the summary's hardcoded 'EUR' default.
  const currency = nrrProgress?.currency ?? getSummaryCurrency(summary);
  const sym = getCurrencySymbol(currency);

  // Display-scoped values. In ARR mode every MRR magnitude is ×12; ratios (bar share/fill)
  // are invariant under the scale so we only annualize the numbers the user reads.
  const displayHeadline = annualize(goalData.current, revenueUnit);
  const displayTarget = annualize(goalData.target, revenueUnit);
  const targetFormatted = `${sym}${fmt(displayTarget)}`;

  // Progress bar segments: each bucket's width = its share of the composition
  // magnitudes (renewal + expansion + pilots). Additive across all three, matches
  // the top-line target — no separate denominator, no floor.
  const barMagnitude =
    goalData.renewalTarget +
    goalData.expansionTarget +
    (pilotsMapped ? goalData.pilotTarget : 0);
  const shareOf = (categoryMagnitude) =>
    barMagnitude > 0 ? (categoryMagnitude / barMagnitude) * 100 : 0;
  const fillOf = (current, categoryTarget) => {
    if (categoryTarget > 0)
      return Math.min(100, (current / categoryTarget) * 100);
    return current > 0 ? 100 : 0;
  };
  const goalSegments = [
    {
      key: "renewal",
      dull: "bg-amber-100",
      solid: "bg-amber-400",
      share: shareOf(goalData.renewalTarget),
      fill: fillOf(goalData.renewalCurrent, goalData.renewalTarget),
    },
    {
      key: "expansion",
      dull: "bg-emerald-100",
      solid: "bg-emerald-500",
      share: shareOf(goalData.expansionTarget),
      fill: fillOf(goalData.expansionCurrent, goalData.expansionTarget),
    },
    // Pilots segment. Auto-hides (share=0, filtered out below) when the team hasn't
    // mapped the HubSpot pilot_end_date custom property (pilots_mapped=false).
    ...(pilotsMapped
      ? [
          {
            key: "pilots",
            dull: "bg-indigo-100",
            solid: "bg-indigo-500",
            share: shareOf(goalData.pilotTarget),
            fill: fillOf(goalData.pilotsCurrent, goalData.pilotTarget),
          },
        ]
      : []),
  ];

  if (loading) {
    return (
      <div className="mb-4" data-testid="goal-card-loading">
        <div className="rounded-[14px] border border-[#e8ecf0] bg-white px-8 py-7">
          <div className="animate-pulse">
            <div className="mb-3 h-4 w-40 rounded bg-slate-200" />
            <div className="mb-5 h-10 w-48 rounded bg-slate-200" />
            <div className="h-3.5 w-full rounded-full bg-slate-100" />
          </div>
        </div>
      </div>
    );
  }

  if (!hasDashboardGoals) {
    return (
      <div className="mb-4">
        <div className="rounded-[14px] border border-[#e8ecf0] bg-white px-8 py-7">
          <div className="flex items-center justify-between">
            <div>
              <div className="mb-1.5 flex items-center gap-2">
                <span className="text-slate-500">
                  <TargetIcon />
                </span>
                <span className="text-[13px] font-semibold uppercase tracking-[0.8px] text-slate-500">
                  {getQuarterLabel()} Revenue Goal
                </span>
              </div>
              <p className="text-sm text-slate-400">
                No team goals configured yet.
              </p>
            </div>
            <button
              onClick={() => setGearModalOpen(true)}
              className="cursor-pointer rounded-[10px] border border-indigo-200 bg-indigo-50 px-4 py-2 text-[13px] font-semibold text-indigo-600 transition-colors hover:bg-indigo-100"
            >
              Set Team Goals
            </button>
          </div>
        </div>
        <TeamGoalsModal
          open={gearModalOpen}
          onClose={() => setGearModalOpen(false)}
          revenueUnit={revenueUnit}
        />
      </div>
    );
  }

  return (
    <>
      <div className="mb-4">
        <div className="relative overflow-visible rounded-[14px] border border-[#e8ecf0] bg-white px-8 py-7">
          <div
            className="pointer-events-none absolute right-0 top-0 h-48 w-48"
            style={{
              background:
                "radial-gradient(circle at right top, rgba(79,70,229,0.04), transparent 70%)",
            }}
          />
          <div className="mb-5 flex items-start justify-between">
            <div>
              <div className="mb-1.5 flex items-center gap-2">
                <span className="text-slate-500">
                  <TargetIcon />
                </span>
                <span className="text-[13px] font-semibold uppercase tracking-[0.8px] text-slate-500">
                  {getQuarterLabel()} Revenue Goal
                </span>
                <button
                  data-testid="eye-button"
                  onClick={() => setEyeModalOpen(true)}
                  className="flex cursor-pointer items-center justify-center rounded-md border border-slate-200 bg-transparent px-2 py-1 text-[11px] font-semibold text-indigo-500 transition-colors hover:bg-slate-50"
                >
                  <EyeIcon />
                </button>
              </div>
              <div className="flex items-baseline gap-3">
                <span className="text-[40px] font-extrabold leading-none tracking-[-1.5px] text-slate-800">
                  {sym}
                  {fmt(displayHeadline)}
                </span>
                <span className="text-base font-medium text-slate-400">
                  {revenueUnit} / {targetFormatted} target
                  {showTopLineEstimatedPill && <EstimatedPill />}
                </span>
              </div>
              {nrrCurrent === 0 && hasPipeline && (
                <p className="mt-1 text-xs text-slate-400">
                  Based on open pipeline — no closed deals yet this quarter
                </p>
              )}
            </div>
            {/* PAN-568 resolved the cohort mismatch — target is now the frozen
                per-quarter goal on the same fiscal quarter as `current`. A
                "remaining to target" surface can be added when we've watched
                the ring behave in production for a quarter. */}
          </div>
          <Tooltip
            placement="bottom"
            arrow={false}
            content={
              <GoalTooltip
                sym={sym}
                goals={[
                  {
                    label: "Renewal",
                    color: "bg-amber-400",
                    current: annualize(
                      goalData.renewalCurrent ?? 0,
                      revenueUnit,
                    ),
                    target: annualize(goalData.renewalTarget, revenueUnit),
                    pipeline: annualize(renewalPipeline, revenueUnit),
                    estimated: estimatedPillEnabled && estimated.renewal,
                  },
                  {
                    label: "Expansion",
                    color: "bg-emerald-500",
                    current: annualize(
                      goalData.expansionCurrent ?? 0,
                      revenueUnit,
                    ),
                    target: annualize(goalData.expansionTarget, revenueUnit),
                    pipeline: annualize(expansionPipeline, revenueUnit),
                    estimated: estimatedPillEnabled && estimated.expansion,
                  },
                  // Pilots tooltip column mirrors the bar-segment auto-hide.
                  ...(pilotsMapped
                    ? [
                        {
                          label: "Pilots",
                          color: "bg-indigo-500",
                          current: annualize(
                            goalData.pilotsCurrent ?? 0,
                            revenueUnit,
                          ),
                          target: annualize(goalData.pilotTarget, revenueUnit),
                          pipeline: annualize(pilotsPipeline, revenueUnit),
                          estimated: estimatedPillEnabled && estimated.pilots,
                        },
                      ]
                    : []),
                ]}
              />
            }
          >
            <div className="flex h-3.5 cursor-pointer overflow-hidden rounded-full bg-slate-100">
              {goalSegments.map((seg, i) =>
                seg.share > 0 ? (
                  <div
                    key={seg.key}
                    className={`h-full ${seg.dull} ${
                      i < goalSegments.length - 1
                        ? "border-r-2 border-white"
                        : ""
                    }`}
                    style={{ width: `${seg.share}%` }}
                  >
                    <div
                      className={`h-full ${seg.solid} transition-all duration-700`}
                      style={{ width: `${seg.fill}%` }}
                    />
                  </div>
                ) : null,
              )}
            </div>
          </Tooltip>
        </div>
      </div>

      <GoalBreakdownModal
        open={eyeModalOpen}
        onClose={() => setEyeModalOpen(false)}
        summary={summary}
        nrrProgress={nrrProgress}
        revenueUnit={revenueUnit}
      />
    </>
  );
};
